import { BlockType } from '../../../models/enums';
import { setCursorPosition, getSelectedRange, captureSelectionState } from '../../../common/utils/selection';
import { getLanguageItems } from '../../../common/utils/data';
import { getBlockModelById, getParentBlock } from '../../../common/utils/block';
import { decoupleReference, getNormalizedKey } from '../../../common/utils/common';
import { events } from '../../../common/constant';
import { BlockFactory } from '../../services/index';
import { createElement } from '@syncfusion/ej2-base';
export class CodeRenderer {
    constructor(manager) {
        this.ctrlAPressed = false;
        this.INDENT_SIZE = 4;
        this.parent = manager;
        this.addEventListeners();
    }
    addEventListeners() {
        this.parent.observer.on(events.keydown, this.handleKeyDownActions, this);
        this.parent.observer.on(events.input, this.handleCodeBlockInput, this);
        this.parent.observer.on(events.localeChanged, this.handleLocaleChange, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.keydown, this.handleKeyDownActions);
        this.parent.observer.off(events.input, this.handleCodeBlockInput);
        this.parent.observer.off(events.localeChanged, this.handleLocaleChange);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    /**
     * Renders a code block
     *
     * @param {BlockModel} block - The block model containing data.
     * @returns {HTMLElement} - The rendered code block element.
     * @hidden
     */
    renderCodeBlock(block) {
        const codeBlockSettings = block.properties;
        const { container, preElement, codeElement } = this.createCodeContainer(block);
        const { toolbar, copyButton } = this.createCodeToolbar();
        this.initializeLanguageSelector(codeBlockSettings, preElement, codeElement, toolbar);
        toolbar.appendChild(copyButton);
        container.appendChild(toolbar);
        container.appendChild(preElement);
        setCursorPosition(codeElement, 0);
        return container;
    }
    createCodeToolbar() {
        const toolbar = createElement('div', {
            className: 'e-code-block-toolbar',
            attrs: { contenteditable: 'false' }
        });
        const copyButton = createElement('button', {
            className: 'e-code-block-copy-button e-btn',
            innerHTML: '<span class="e-icons e-copy"></span>',
            attrs: {
                title: this.parent.localeJson['codeCopyTooltip'],
                type: 'button',
                contenteditable: 'false'
            }
        });
        this.addCopyButtonClick(toolbar, copyButton);
        return { toolbar, copyButton };
    }
    addCopyButtonClick(toolbar, copyButton) {
        copyButton.addEventListener('click', () => {
            const codeElement = toolbar.closest('.e-code-block-container').querySelector('code');
            if (codeElement) {
                const codeText = codeElement.textContent;
                window.navigator.clipboard.writeText(codeText)
                    .then(() => {
                    const iconElement = copyButton.querySelector('.e-icons');
                    if (iconElement) {
                        const originalClass = iconElement.className;
                        iconElement.className = 'e-icons e-check-tick';
                        setTimeout(() => {
                            iconElement.className = originalClass;
                        }, 2000);
                    }
                })
                    .catch((err) => {
                    console.error('Could not copy text: ', err);
                });
            }
        });
    }
    initializeLanguageSelector(codeBlockSettings, preElement, codeElement, targetElement) {
        const languageDataSource = getLanguageItems();
        if (this.parent.codeBlockSettings.languages.length === 0) {
            this.parent.codeBlockSettings.languages = languageDataSource;
        }
        this.renderDropDown(codeBlockSettings, preElement, codeElement, targetElement);
    }
    renderDropDown(codeBlockSettings, preElement, codeElement, targetElement) {
        this.parent.observer.notify('renderDropdownList', {
            targetElement: targetElement,
            dataSource: this.parent.codeBlockSettings.languages,
            fields: { text: 'label', value: 'language' },
            value: codeBlockSettings.language || this.parent.codeBlockSettings.defaultLanguage,
            change: (e) => {
                const newLanguage = e.value;
                if (codeElement && preElement) {
                    codeElement.className = `e-code-content e-block-content language-${newLanguage}`;
                    preElement.setAttribute('data-language', newLanguage);
                    // Update in Model
                    codeBlockSettings.language = newLanguage;
                }
            }
        });
    }
    createCodeContainer(block) {
        const container = createElement('div', { className: 'e-code-block-container' });
        const preElement = createElement('pre', {
            className: 'e-code-block',
            attrs: {
                'data-language': this.parent.codeBlockSettings.defaultLanguage
            }
        });
        const hasContent = block.content && block.content.length > 0;
        const codeElement = createElement('code', {
            className: `e-code-content e-block-content language-${this.parent.codeBlockSettings.defaultLanguage}`,
            attrs: { contenteditable: 'true' }
        });
        if (hasContent && block.content[0].content) {
            codeElement.textContent = block.content[0].content;
        }
        else {
            codeElement.textContent = '\n';
        }
        preElement.appendChild(codeElement);
        return { container, preElement, codeElement };
    }
    handleKeyDownActions(e) {
        if (!this.parent.currentFocusedBlock) {
            return;
        }
        const codeElement = this.parent.currentFocusedBlock.querySelector('code');
        const block = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        if (!codeElement || (block && block.blockType !== BlockType.Code)) {
            return;
        }
        const normalizedKey = getNormalizedKey(e);
        if (normalizedKey === 'ctrl+a') {
            this.handleCtrlASelection(e, codeElement);
            return;
        }
        switch (e.key) {
            case 'Enter':
                this.handleEnterKey(e, codeElement, block);
                break;
            case 'Backspace':
                this.handleDeletion(e, codeElement, block, false);
                break;
            case 'Delete':
                this.handleDeletion(e, codeElement, block, true);
                break;
            case 'Tab':
                this.handleTabKey(e, codeElement);
                break;
        }
    }
    handleEnterKey(e, codeElement, block) {
        e.preventDefault();
        this.parent.previousSelection = captureSelectionState();
        const action = this.determineEnterAction(codeElement);
        if (action.shouldExit) {
            this.exitCodeBlock(codeElement, block);
            return;
        }
        // Store the old block state for undo/redo tracking
        const oldBlockModel = decoupleReference(block);
        // Insert single newline with indentation
        this.insertTextAtCursor(action.indentation);
        // Update the block model after inserting line breaks
        this.updateBlockModel(codeElement, block);
        // Track the change for undo/redo
        const clonedBlock = decoupleReference(block);
        this.parent.undoRedoAction.trackContentChangedForUndoRedo(oldBlockModel, clonedBlock);
    }
    determineEnterAction(codeElement) {
        const cursorPosition = this.getCursorPosition(codeElement);
        const indentation = this.getCurrentLineIndentation(codeElement, cursorPosition);
        const shouldExit = this.shouldExitCodeBlock(codeElement);
        return { shouldExit, indentation };
    }
    handleDeletion(e, codeElement, block, isDeleteKey) {
        const cursorPosition = this.getCursorPosition(codeElement);
        const textContent = codeElement.textContent;
        const range = getSelectedRange();
        const shouldPreventDefault = isDeleteKey
            ? (cursorPosition >= textContent.length)
            : (cursorPosition === 0 || textContent.length === 0);
        if ((textContent.length === 1) || (range.toString() === textContent)) {
            e.preventDefault();
            // Store the old block state for undo/redo tracking
            const oldBlockModel = decoupleReference(block);
            codeElement.textContent = '\n';
            setCursorPosition(codeElement, 0);
            this.updateBlockModel(codeElement, block);
            // Track the change for undo/redo
            const clonedBlock = decoupleReference(block);
            this.parent.undoRedoAction.trackContentChangedForUndoRedo(oldBlockModel, clonedBlock);
        }
        if (shouldPreventDefault) {
            e.preventDefault();
            return;
        }
    }
    handleTabKey(e, codeElement) {
        e.preventDefault();
        this.parent.previousSelection = captureSelectionState();
        const block = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        if (!block) {
            return;
        }
        // Store the old block state for undo/redo tracking
        const oldBlockModel = decoupleReference(block);
        if (e.shiftKey) {
            this.removeIndentation(codeElement);
        }
        else {
            this.addIndentation();
        }
        // Update block model after indentation change
        this.updateBlockModel(codeElement, block);
        // Track the change for undo/redo
        const clonedBlock = decoupleReference(block);
        this.parent.undoRedoAction.trackContentChangedForUndoRedo(oldBlockModel, clonedBlock);
    }
    handleCodeBlockInput(e) {
        if (!this.parent.currentFocusedBlock) {
            return;
        }
        const codeElement = this.parent.currentFocusedBlock.querySelector('code');
        const block = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        if (!codeElement || block.blockType !== BlockType.Code) {
            return;
        }
        // Store the old block state for undo/redo tracking
        const oldBlockModel = decoupleReference(block);
        const textContent = codeElement.textContent || '';
        if (!textContent.trim() && codeElement.textContent !== '\n') {
            codeElement.textContent = '\n';
        }
        this.updateBlockModel(codeElement, block);
        // Track the change for undo/redo and notify block change event
        const clonedBlock = decoupleReference(block);
        this.parent.undoRedoAction.trackContentChangedForUndoRedo(oldBlockModel, clonedBlock);
        this.parent.eventService.addChange({
            action: 'Update',
            data: {
                block: clonedBlock,
                prevBlock: oldBlockModel
            }
        });
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    handleCtrlASelection(e, codeElement) {
        if (!this.ctrlAPressed) {
            e.preventDefault();
            this.selectEntireCodeBlock(codeElement);
            this.ctrlAPressed = true;
            setTimeout(() => {
                this.ctrlAPressed = false;
            }, 500);
        }
        else {
            this.ctrlAPressed = false;
        }
    }
    getCursorPosition(element) {
        const range = this.parent.nodeSelection.getRange();
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
    }
    insertTextAtCursor(indentValue) {
        const selection = window.getSelection();
        if (!selection.rangeCount) {
            return;
        }
        const codeElement = this.parent.currentFocusedBlock.querySelector('code');
        if (!codeElement) {
            return;
        }
        const range = selection.getRangeAt(0);
        const cursorPositionBefore = this.getCursorPosition(codeElement);
        const textContent = codeElement.textContent || '';
        // 1. Delete any selected text
        range.deleteContents();
        // 2. Determine if we're truly at the document end
        const atEnd = cursorPositionBefore >= textContent.length;
        let newlineToInsert = '\n';
        if (atEnd) {
            // Check the character right before the cursor (if any)
            if (cursorPositionBefore > 0 && textContent[cursorPositionBefore - 1] === '\n') {
                // Already ends with newline → just one more \n
                newlineToInsert = '\n';
            }
            else {
                // Does NOT end with newline → most editors insert double newline
                newlineToInsert = '\n\n';
            }
        }
        const textToInsert = newlineToInsert + (indentValue || '');
        const textNode = document.createTextNode(textToInsert);
        range.insertNode(textNode);
        // Calculate new cursor position and set it
        const newCursorPosition = cursorPositionBefore + textToInsert.length;
        codeElement.normalize();
        setCursorPosition(codeElement, newCursorPosition);
    }
    getCurrentLineIndentation(element, cursorPosition) {
        const textContent = element.textContent;
        const lines = textContent.substring(0, cursorPosition).split('\n');
        const currentLine = lines[lines.length - 1];
        const indentMatch = currentLine.match(/^(\s*)/);
        return indentMatch ? indentMatch[1] : '';
    }
    shouldExitCodeBlock(element) {
        const textContent = element.textContent;
        const cursorPosition = this.getCursorPosition(element);
        // Check if cursor is at the end of content
        if (cursorPosition !== textContent.length) {
            return false;
        }
        // Exit only when content ends with three newlines (user pressed Enter 3 times at the end)
        // This means there are two empty lines at the end
        return textContent.endsWith('\n\n\n');
    }
    exitCodeBlock(codeElement, block) {
        const blockElement = getParentBlock(codeElement);
        setTimeout(() => {
            const nextSibling = blockElement.nextElementSibling;
            if (nextSibling) {
                this.parent.setFocusAndUIForNewBlock(nextSibling);
            }
            else {
                const newBlock = BlockFactory.createParagraphBlock();
                this.parent.blockCommand.addBlock({ block: newBlock, targetBlock: blockElement });
            }
        });
    }
    addIndentation() {
        const indent = ' '.repeat(this.INDENT_SIZE);
        const range = this.parent.nodeSelection.getRange();
        range.insertNode(document.createTextNode(indent));
        const endContainer = range.endContainer;
        setCursorPosition(endContainer, range.startOffset + indent.length);
    }
    removeIndentation(element) {
        const cursorPosition = this.getCursorPosition(element);
        const textContent = element.textContent;
        // Check for spaces right before the cursor position
        const startCheckPos = Math.max(0, cursorPosition - this.INDENT_SIZE);
        const beforeCursor = textContent.substring(startCheckPos, cursorPosition);
        const spacesBeforeCursor = beforeCursor.match(/\s*$/)[0].length;
        if (spacesBeforeCursor > 0) {
            const removeStart = cursorPosition - spacesBeforeCursor;
            element.textContent = textContent.substring(0, removeStart) + textContent.substring(cursorPosition);
            setCursorPosition(element, removeStart);
        }
    }
    selectEntireCodeBlock(element) {
        const range = document.createRange();
        range.selectNodeContents(element);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
    }
    updateBlockModel(codeElement, block) {
        // Since we're using literal newlines now, just use textContent directly
        let textContent = codeElement.textContent || '';
        // Remove trailing newline if it's the only character or if there are multiple trailing newlines,
        // keep only the structure we want
        if (textContent === '\n') {
            textContent = '';
        }
        else if (textContent.endsWith('\n')) {
            // Preserve newlines in the middle of content, but trim excessive trailing newlines
            textContent = textContent.replace(/\n+$/, '');
        }
        if (!block.content || block.content.length === 0) {
            this.parent.blockService.replaceBlock(block.id, Object.assign({}, decoupleReference(block), { content: [BlockFactory.createTextContent({ content: textContent })] }));
        }
        else {
            block.content[0].content = textContent;
        }
        this.parent.stateManager.updateManagerBlocks();
    }
    handleLocaleChange() {
        const codeBlocks = this.parent.rootEditorElement.querySelectorAll('.e-code-block-container');
        for (const codeBlock of Array.from(codeBlocks)) {
            const copyButton = codeBlock.querySelector('.e-code-block-copy-button');
            if (copyButton) {
                copyButton.setAttribute('title', this.parent.localeJson['codeCopyTooltip']);
            }
        }
    }
    destroy() {
        this.removeEventListeners();
    }
}
